import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class Loader {
  // Reference-counted: every show() increments, every hide() decrements.
  // The skeleton state (loading$) is gated by the "loading window" below —
  // ref-counting alone is not enough, because every HTTP call (popup-open,
  // button-click, telemetry) would otherwise flash the page-level skeleton.
  private count = 0;
  private _loading = new BehaviorSubject<boolean>(false);

  // Loading window: opens at NavigationStart, closes once the initial load
  // wave settles (count → 0 after NavigationEnd). While open, show() emits
  // true. While closed, show() still increments count for symmetry but does
  // NOT emit — so in-page HTTP calls AFTER initial load are invisible to the
  // skeleton. This is the fix for "skeleton flashes when opening a popup".
  private windowOpen = true;
  private navigationEnded = false;
  private autoCloseTimer: any = null;

  loading$ = this._loading.asObservable();
  readonly isLoading = toSignal(this._loading, { initialValue: false });

  constructor(private router: Router) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.openWindow();
      } else if (event instanceof NavigationEnd) {
        this.onNavigationEnd();
      }
    });
  }

  show() {
    this.count++;
    if (this.windowOpen && !this._loading.value) {
      this._loading.next(true);
    }
  }

  hide() {
    this.count = Math.max(0, this.count - 1);
    if (this.count !== 0) return;

    if (this._loading.value) {
      this._loading.next(false);
    }
    // Only lock the window AFTER NavigationEnd — otherwise a guard's HTTP
    // finishing mid-navigation would close it before ngOnInit's HTTP fires,
    // and the new screen would silently load without showing a skeleton.
    if (this.navigationEnded) {
      this.windowOpen = false;
      this.clearAutoClose();
    }
  }

  /** Force-clear. Reserved for unusual cleanup paths (e.g. error recovery). */
  reset() {
    this.count = 0;
    if (this._loading.value) {
      this._loading.next(false);
    }
    this.windowOpen = false;
    this.clearAutoClose();
  }

  private openWindow() {
    this.clearAutoClose();
    // Hard-clear: stale state from the previous screen (including stuck
    // ref-counts) does not bleed into the new navigation. The interceptor's
    // 60s watchdog covers truly hung requests so this reset is safe.
    this.count = 0;
    if (this._loading.value) {
      this._loading.next(false);
    }
    this.windowOpen = true;
    this.navigationEnded = false;
  }

  private onNavigationEnd() {
    this.navigationEnded = true;
    // Safety net: routes with no API calls (e.g. /noAccess) would otherwise
    // leave the window armed indefinitely, causing one stray skeleton flash
    // on the user's first in-page action. Give ngOnInit one tick to register
    // its HTTP, then auto-close if nothing fired.
    this.clearAutoClose();
    this.autoCloseTimer = setTimeout(() => {
      if (this.count === 0) {
        this.windowOpen = false;
      }
      this.autoCloseTimer = null;
    }, 100);
  }

  private clearAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }
}
